import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FaBuilding, FaChevronRight, FaPlus } from "react-icons/fa"
import { Avatar, AvatarFallback } from "@/modules/shared/ui/components/avatar"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Button } from "@/modules/shared/ui/components/button"
import { Separator } from "@/modules/shared/ui/components/separator"
import { Skeleton } from "@/modules/shared/ui/components/skeleton"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { Organization } from "@/modules/organizations/entities/Organization"
import { OrganizationStatus } from "@/modules/organizations/enums/organization-status"
import RegisterOrganizationModal from "@/modules/dashboard/ui/components/RegisterOrganizationModal"
import ReactivatePlanModal from "@/modules/subscriptions/ui/components/ReactivatePlanModal"
import ActivateSubscriptionModal from "@/modules/subscriptions/ui/components/ActivateSubscriptionModal"
import { SmartLogo } from "@/modules/shared/ui/components/custom/SmartLogo"
import { ThemeToggle } from "@/modules/shared/ui/components/theme-toggle"

const PLAN_COLOURS: Record<string, string> = {
  Recruitment: "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300",
  Onboarding: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
  Both: "bg-primary/10 text-primary",
}

const ORG_ACTION = {
  register: 'register',
  reactivate: 'reactivate',
  activate: 'activate',
} as const

type ActiveModal = typeof ORG_ACTION[keyof typeof ORG_ACTION] | null

export default function SelectOrganisationPage() {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null)
  const selectedOrg = useRef<Organization | null>(null)
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { organizations, loadOrganizations, selectOrganization, isLoading } =
    useOrganizationsService()

  const displayName =
    `${user?.user.firstName ?? ""} ${user?.user.lastName ?? ""}`.trim()

  useEffect(() => {
    loadOrganizations().catch(() => {
      // silently fail — errors shown via toast elsewhere
    })
  }, [loadOrganizations])

  const navigateForOrg = () => {
    navigate("/dashboard", { replace: true })
  }

  const handleSelect = (org: Organization) => {
    if (org.status === OrganizationStatus.Inactive) {
      selectedOrg.current = org
      setActiveModal(ORG_ACTION.reactivate)
      return
    }
    if (org.status === OrganizationStatus.Pending) {
      selectedOrg.current = org
      setActiveModal(ORG_ACTION.activate)
      return
    }
    selectOrganization(org)
    navigateForOrg()
  }

  return (
    <div className="min-h-screen flex flex-col bg-layout-bg">
      {/* Top bar */}
      <header className="flex items-center justify-between px-8 py-4 bg-card border-b border-border">
        <SmartLogo size="md" className="w-36 dark:brightness-0 dark:invert" />
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-muted-foreground hover:text-foreground"
          >
            Sign out
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          {/* Greeting */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/15 dark:bg-primary/25 mb-4">
              <FaBuilding className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              {displayName ? `Welcome back, ${displayName}` : "Welcome back!"}
            </h1>
            <p className="mt-2 text-muted-foreground">
              Select an organisation to continue
            </p>
          </div>

          {/* Organisation list */}
          <div className="bg-card rounded-2xl shadow-md border border-border overflow-hidden">
            {isLoading ? (
              <div className="divide-y divide-border">
                {[1, 2].map((i) => (
                  <div key={i} className="px-5 py-4 flex items-center gap-4">
                    <Skeleton className="h-10 w-10 rounded-xl shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                ))}
              </div>
            ) : organizations.length === 0 ? (
              <div className="px-5 py-10 text-center text-muted-foreground text-sm">
                No organisations found.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {organizations.map((org) => {
                  const isActive = org.status === OrganizationStatus.Active
                  const isInactive = org.status === OrganizationStatus.Inactive
                  const isPending = org.status === OrganizationStatus.Pending
                  const isClickable = isActive || isInactive || isPending
                  return (
                    <li key={org.id}>
                      <Button
                        variant="ghost"
                        onClick={() => isClickable && handleSelect(org)}
                        disabled={!isClickable}
                        className={`w-full flex items-center gap-4 px-5 py-4 h-auto rounded-none justify-start transition-colors
                          ${isClickable
                            ? "hover:bg-muted"
                            : "opacity-40 cursor-not-allowed"
                          }`}
                      >
                        <Avatar className="h-10 w-10 shrink-0">
                          <AvatarFallback className="rounded-xl text-sm font-bold bg-primary/15 dark:bg-primary/25 text-primary">
                            {org.name?.[0]?.toUpperCase() ?? "O"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0 text-left">
                          <p className="text-sm font-semibold text-foreground truncate">
                            {org.name}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge
                              variant="secondary"
                              className={`text-[10px] px-2 py-0 border-0 font-medium ${PLAN_COLOURS[org.plan] ?? "bg-muted text-muted-foreground"}`}
                            >
                              {org.plan}
                            </Badge>
                            {!isActive && (
                              <Badge
                                variant="secondary"
                                className={`text-[10px] px-2 py-0 border-0 font-medium ${
                                  isInactive
                                  ? "bg-destructive/10 text-destructive"
                                  : "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400"
                                }`}
                              >
                                {isInactive ? "Trial expired" : "Activate subscription"}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <FaChevronRight
                          className={`h-3.5 w-3.5 shrink-0 ${isInactive
                              ? "text-destructive"
                              : isPending
                                ? "text-amber-500 dark:text-amber-400"
                                : "text-muted-foreground"
                            }`}
                        />
                      </Button>
                    </li>
                  )
                })}
              </ul>
            )}

            {/* Create new org */}
            <Separator />
            <Button
              variant="ghost"
              onClick={() => setActiveModal(ORG_ACTION.register)}
              className="w-full flex items-center gap-4 px-5 py-4 h-auto rounded-none justify-start hover:bg-muted transition-colors"
            >
              <div className="h-10 w-10 rounded-xl border-2 border-dashed border-border flex items-center justify-center shrink-0">
                <FaPlus className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold text-foreground">
                  Create a new organisation
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Set up a new workspace
                </p>
              </div>
              <FaChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            </Button>
          </div>

          {/* ── Modals ── */}
          <RegisterOrganizationModal
            isOpen={activeModal === ORG_ACTION.register}
            onClose={() => setActiveModal(null)}
          />

          <ReactivatePlanModal
            organization={selectedOrg.current}
            isOpen={activeModal === ORG_ACTION.reactivate}
            onClose={() => setActiveModal(null)}
          />

          <ActivateSubscriptionModal
            organization={selectedOrg.current}
            isOpen={activeModal === ORG_ACTION.activate}
            onClose={() => setActiveModal(null)}
            onSubscriptionActivated={() => {
              loadOrganizations().catch(() => { })
            }}
          />
        </div>
      </main>
    </div>
  )
}
